/**
 * Phone numbers. Everything is stored and compared as E.164 (+1NPANXXXXXX). normalizePhone
 * accepts any well-formed North American Numbering Plan number (inbound texts, our own
 * number); the join form and the People list also run checkUsMobile, because the MVP texts
 * from a US toll-free number, registered for US recipients only.
 */

/** E.164 for a NANP number, or null when it is not a valid one. */
export function normalizePhone(input: string): string | null {
  const trimmed = input.trim()
  if (!/^[+\d\s().-]+$/.test(trimmed)) return null
  let digits = trimmed.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
  if (digits.length !== 10) return null
  // Area code and exchange both start 2-9 (NANP).
  if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return null
  return `+1${digits}`
}

/** "(•••) •••-1234": what any screen shows, so a full number is never on display. */
export function maskPhone(e164: string): string {
  const last4 = e164.slice(-4)
  return `(•••) •••-${last4}`
}

/** "(555) 555-0123", for the dev phone simulator only. */
export function formatPhone(e164: string): string {
  const d = e164.replace(/^\+1/, '')
  if (d.length !== 10) return e164
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

/** The first word of a name: what other contacts see. */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? ''
}

/**
 * NANP area codes outside the 50 states and DC: Canada (geographic and non-geographic), the
 * Caribbean and Atlantic countries, and the US territories (a US toll-free number does not
 * reliably reach them). From the NANPA area code list; small enough to keep here.
 */
const NON_US_AREA_CODES = new Set(
  [
    // Canada
    '204 226 236 249 250 257 263 289 306 343 354 365 367 368 382 387 403 416 418 428 431 437',
    '438 450 460 468 474 506 514 519 548 579 581 584 587 600 604 613 622 633 639 644 647 655',
    '672 677 683 688 705 709 742 753 778 780 782 807 819 825 867 873 879 902 905 942',
    // Caribbean and Atlantic countries
    '242 246 264 268 284 345 441 473 649 658 664 721 758 767 784 809 829 849 868 869 876',
    // US territories: USVI, Northern Mariana Islands, Guam, American Samoa, Puerto Rico
    '340 670 671 684 787 939',
  ]
    .join(' ')
    .split(' '),
)

/**
 * Area codes that are never a mobile line: toll-free, premium, personal and other
 * non-geographic service codes. (N11, N9X and 37X/96X are refused by pattern.)
 */
const SERVICE_AREA_CODES = new Set(
  '456 500 521 522 523 524 525 526 527 528 529 532 533 535 544 566 577 588 700 710 800 822 833 844 855 866 877 880 881 882 883 884 885 886 887 888 889 900'.split(
    ' ',
  ),
)

/**
 * The fictional ranges: area code 555, and 555-0100 through 555-0199 in any area code (set
 * aside for films and examples). Tests and the dev phone simulator use them.
 */
export function isFictionalPhone(e164: string): boolean {
  const d = e164.replace(/^\+1/, '')
  return d.startsWith('555') || /^\d{3}55501\d{2}$/.test(d)
}

/**
 * Why this E.164 number cannot get texts, or null when it can: 'not_us' outside the 50 states
 * and DC, 'invalid' for numbers that cannot be a real mobile line. `allowFictional` lets the
 * 555 ranges through (development, tests, and the demo join page, which sends nothing).
 */
export function checkUsMobile(
  e164: string,
  opts: { allowFictional?: boolean } = {},
): 'not_us' | 'invalid' | null {
  const m = /^\+1([2-9]\d{2})([2-9]\d{2})(\d{4})$/.exec(e164)
  if (!m) return 'invalid'
  const [, area = '', exchange = ''] = m
  if (isFictionalPhone(e164)) return opts.allowFictional ? null : 'invalid'
  if (NON_US_AREA_CODES.has(area)) return 'not_us'
  if (
    SERVICE_AREA_CODES.has(area) ||
    area.slice(1) === '11' || // N11: 211, 311 ... 911
    area[1] === '9' || // N9X: reserved for expansion
    /^(37|96)\d$/.test(area) || // reserved blocks
    exchange.slice(1) === '11' // N11 exchanges are service codes, not lines
  ) {
    return 'invalid'
  }
  return null
}

/** What the join form and the People list say for each checkUsMobile answer. */
/**
 * Texts-off mode (the API's TEXTS_ENABLED, MeResponse.textsEnabled): the answer to a number
 * sent anyway, and the line on pages that would otherwise offer texts.
 */
export const TEXTS_UNAVAILABLE =
  'Text updates aren’t available yet. Leave the number blank to get updates by email.'
export const TEXTS_COMING_SOON = 'Text updates are coming soon.'

export const US_MOBILE_MESSAGES = {
  invalid: 'Enter a valid 10-digit US mobile number.',
  not_us: 'Text updates are only for US mobile numbers for now.',
} as const
